import React from 'react';
import { Github, Linkedin, Twitter, Link as LinkIcon, Mail, MapPin, Phone, ExternalLink, Sparkles, Code, Briefcase, GraduationCap, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function NeonGlowTheme({ profileData, socials, activeColor, trackSocialClick, trackDownload }) {
  const { core, education = [], experience = [], projects = [], skills = [], languages = [], awards = [] } = profileData;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white font-sans selection:bg-cyan-500/30 overflow-hidden">
      {/* Animated background grid */}
      <div className="fixed inset-0 z-0 opacity-15" style={{
        backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Floating neon orbs */}
      <div className="fixed top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" />
      <div className="fixed bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="fixed top-1/2 left-1/2 w-64 h-64 bg-pink-500/8 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Hero */}
      <section className="relative z-10 min-h-[90vh] flex items-center justify-center px-6">
        <div className="text-center space-y-8 max-w-3xl">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 text-cyan-400 text-xs font-bold tracking-widest uppercase"
            style={{ boxShadow: '0 0 20px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.05)' }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Open to Work</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-5xl sm:text-7xl font-black tracking-tight"
            style={{ textShadow: '0 0 40px rgba(0, 255, 255, 0.3), 0 0 80px rgba(0, 255, 255, 0.1)' }}
          >
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              {core.name}
            </span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
          >
            {core.summary || 'Creative professional with a passion for building exceptional digital experiences.'}
          </motion.p>

          {/* Neon social links */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="flex justify-center gap-4"
          >
            {socials.github && (
              <a href={socials.github} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('github')}
                className="p-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/40 transition-all hover:shadow-[0_0_20px_rgba(0,255,255,0.2)]">
                <Github className="w-5 h-5" />
              </a>
            )}
            {socials.linkedin && (
              <a href={socials.linkedin} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('linkedin')}
                className="p-3 rounded-xl border border-blue-500/20 bg-blue-500/5 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/40 transition-all hover:shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                <Linkedin className="w-5 h-5" />
              </a>
            )}
            {socials.twitter && (
              <a href={socials.twitter} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('twitter')}
                className="p-3 rounded-xl border border-purple-500/20 bg-purple-500/5 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/40 transition-all hover:shadow-[0_0_20px_rgba(168,85,247,0.2)]">
                <Twitter className="w-5 h-5" />
              </a>
            )}
            {socials.custom && (
              <a href={socials.custom} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('custom')}
                className="p-3 rounded-xl border border-pink-500/20 bg-pink-500/5 text-pink-400 hover:bg-pink-500/10 hover:border-pink-500/40 transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.2)]">
                <LinkIcon className="w-5 h-5" />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* Experience */}
      {experience.length > 0 && (
        <section className="relative z-10 max-w-4xl mx-auto px-6 py-20">
          <motion.h2 {...fadeIn} className="text-2xl font-bold mb-10 text-center"
            style={{ textShadow: '0 0 20px rgba(0, 255, 255, 0.2)' }}
          >
            <span className="text-cyan-400">{'< '}</span>Experience<span className="text-cyan-400">{' />'}</span>
          </motion.h2>

          <div className="space-y-6">
            {experience.map((exp, i) => (
              <motion.div key={i} {...fadeIn}
                className="p-6 rounded-2xl border border-cyan-500/10 bg-cyan-500/[0.02] hover:border-cyan-500/25 transition-all backdrop-blur-sm"
                style={{ boxShadow: 'inset 0 1px 0 rgba(0,255,255,0.05)' }}
              >
                <div className="text-[10px] font-bold text-cyan-400/60 uppercase tracking-widest mb-2">
                  {exp.start_date} — {exp.end_date || 'Present'}
                </div>
                <h3 className="text-lg font-bold text-white">{exp.job_title}</h3>
                <div className="text-sm text-cyan-400/80 font-semibold mb-2">{exp.company}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{exp.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <section className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          <motion.h2 {...fadeIn} className="text-2xl font-bold mb-10 text-center"
            style={{ textShadow: '0 0 20px rgba(168, 85, 247, 0.2)' }}
          >
            <span className="text-purple-400">{'{ '}</span>Projects<span className="text-purple-400">{' }'}</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.map((proj, i) => (
              <motion.div key={i} {...fadeIn}
                className="group p-6 rounded-2xl border border-purple-500/10 bg-purple-500/[0.02] hover:border-purple-500/30 transition-all"
                style={{ boxShadow: 'inset 0 1px 0 rgba(168,85,247,0.05)' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <Code className="w-4 h-4 text-purple-400" />
                  </div>
                  <h3 className="text-base font-bold text-white pt-1">{proj.title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{proj.description}</p>
                {proj.link && (
                  <a href={proj.link} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-purple-400 hover:text-purple-300 transition">
                    View Project <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <section className="relative z-10 max-w-4xl mx-auto px-6 py-20">
          <motion.h2 {...fadeIn} className="text-2xl font-bold mb-10 text-center"
            style={{ textShadow: '0 0 20px rgba(236, 72, 153, 0.2)' }}
          >
            <span className="text-pink-400">{'[ '}</span>Skills<span className="text-pink-400">{' ]'}</span>
          </motion.h2>

          <motion.div {...fadeIn} className="flex flex-wrap justify-center gap-3">
            {skills.map((sk, i) => (
              <span key={i}
                className="px-4 py-2 rounded-full text-xs font-bold border border-pink-500/15 bg-pink-500/[0.03] text-pink-300/80 hover:border-pink-500/30 hover:bg-pink-500/[0.06] transition-all cursor-default"
                style={{ boxShadow: '0 0 10px rgba(236, 72, 153, 0.05)' }}
              >
                {sk.name || sk}
              </span>
            ))}
          </motion.div>
        </section>
      )}

      {/* Contact Footer */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 py-20 text-center space-y-6">
        <motion.h2 {...fadeIn} className="text-xl font-bold text-slate-300">Let's Connect</motion.h2>
        <motion.div {...fadeIn} className="flex flex-wrap justify-center gap-6 text-xs text-slate-400">
          {core.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-cyan-400" />{core.email}</span>}
          {core.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-cyan-400" />{core.phone}</span>}
          {core.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-cyan-400" />{core.city}, {core.country}</span>}
        </motion.div>
        <motion.p {...fadeIn} className="text-[10px] text-slate-600 pt-8">
          Built with <span className="text-cyan-500">CVLogs</span> Portfolio Builder
        </motion.p>
      </section>
    </div>
  );
}
